import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import LoadThemeComponent from '../../../../base/LoadThemeComponent';
import { convertAmount, getFractionDigits } from '../../../../utils/formatters';
import { shouldUseLineBuyPrice } from '../../helpers/linesHelper';
import { supportStakes } from '../../helpers/pickHelper';
import useLinesHelper from '../../helpers/useLinesHelper';
import { RootState } from '../../redux/store';

type Props = {
    index: number;
    stake: number;
    stakeMin: number;
    stakeMax: number;
    readonly linePriceGross: number;
    setLineStake: Function;
    ticketPriceMin: number | null | undefined;
    ticketPriceMax: number | null | undefined;
};

function LinePrice({
    index,
    stake,
    stakeMin,
    stakeMax,
    linePriceGross,
    setLineStake,
    ticketPriceMin,
    ticketPriceMax,
}: Props) {
    const { t } = useTranslation();
    const { lottery, ticketPrice } = useSelector((state: RootState) => state.playStore);
    const lineSellPriceGross = shouldUseLineBuyPrice(lottery.currencyCode)
        ? lottery.lotteryRule.line_buy_price
        : linePriceGross;
    const [price, setPrice] = useState<number>(lineSellPriceGross * stake);
    const [inputStakeValue, setInputStakeValue] = useState<number | null>(lineSellPriceGross * stake);
    const [minPrice, setMinPrice] = useState<number>(stakeMin * lineSellPriceGross);
    const [maxPrice, setMaxPrice] = useState<number>(stakeMax * lineSellPriceGross);
    const re = getFractionDigits() === 0 ? /^[0-9]+$/ : /^[0-9]+(\.[0-9]+)?$/;
    const { setStakeIsValid } = useLinesHelper();

    function setPriceIfValid(val: string) {
        const maxLength = 9;
        let amount = val;
        if (amount.length > maxLength) {
            amount = amount.substring(0, maxLength);
        }

        if (re.test(amount)) {
            const valInt = roundToFraction(parseFloat(amount));
            if (valInt >= minPrice && valInt <= maxPrice) {
                setPrice(valInt);
            }

            if (valInt > maxPrice || valInt < minPrice) {
                setStakeIsValid(false);
            } else {
                setStakeIsValid(true);
            }
        }

        if (amount === '' || re.test(amount)) {
            setInputStakeValue(amount === '' ? null : roundToFraction(parseFloat(amount)));
        }
    }

    // works good for FCFA
    // if not works for EUR just return num or check if (line price / stake)<1
    function roundToFraction(num: number, fractionDigits = 2) {
        const factor = 10 ** fractionDigits;
        return Math.round(num * factor) / factor;
    }

    useEffect(() => {
        setMinPrice(stakeMin * lineSellPriceGross);
        setMaxPrice(stakeMax * lineSellPriceGross);
    }, [linePriceGross, stakeMin, stakeMax]); // because initiated with 1 by session lines

    useEffect(() => {
        setPriceIfValid(String(lineSellPriceGross * stake));
    }, [stake, minPrice, maxPrice]); // because recycling on "clear"

    useEffect(() => {
        setLineStake(index, price / lineSellPriceGross); //  Ex. selected 20 USD / 10 USD = 2
    }, [price]);

    const validatePrice = () => {
        if (inputStakeValue > maxPrice) {
            return (
                <p className="text-danger">
                    {t('playPicker.permsAndBankers.maxStake', {
                        maxStake: convertAmount(maxPrice, lottery.currencyCode),
                    })}
                </p>
            );
        }

        if (inputStakeValue < minPrice) {
            return (
                <p className="text-danger">
                    {t('playPicker.permsAndBankers.minStake', {
                        minStake: convertAmount(minPrice, lottery.currencyCode),
                    })}
                </p>
            );
        }

        if (ticketPriceMin !== null && ticketPrice !== 0 && ticketPrice < ticketPriceMin) {
            return (
                <p className="text-danger">
                    {t('playPicker.minTicketPrice', { minPrice: convertAmount(ticketPriceMin, lottery.currencyCode) })}
                </p>
            );
        }

        if (ticketPriceMax !== null && ticketPrice !== 0 && ticketPrice > ticketPriceMax) {
            return (
                <p className="text-danger">
                    {t('playPicker.maxTicketPrice', { maxPrice: convertAmount(ticketPriceMax, lottery.currencyCode) })}
                </p>
            );
        }

        return null;
    };

    const baseView = () =>
        supportStakes(stakeMin, stakeMax) && (
            <div className="col-auto line-price mt-5 text-center">
                <p>{t('playPicker.permsAndBankers.setStake')}</p>
                <input
                    type="text"
                    className={
                        inputStakeValue < minPrice || inputStakeValue > maxPrice
                            ? 'text-danger'
                            : 'line-price-text-input'
                    }
                    value={inputStakeValue ?? 0}
                    onChange={(event) => setPriceIfValid(event.target.value ?? '')}
                />

                {validatePrice()}
            </div>
        );

    const defaultView = () =>
        supportStakes(stakeMin, stakeMax) && (
            <div className="col-auto line-price text-center d-flex flex-column align-items-center g-0">
                <div className="stake-container d-flex align-items-center">
                    <p className="mb-0">Stake:</p>
                    <input
                        type="text"
                        className={
                            inputStakeValue < minPrice || inputStakeValue > maxPrice
                                ? 'line-price-text-input text-danger'
                                : 'line-price-text-input'
                        }
                        value={inputStakeValue ?? 0}
                        onChange={(event) => setPriceIfValid(event.target.value ?? '')}
                    />
                    {/* {validatePrice() */}
                </div>
            </div>
        );

    function getContent() {
        if (minPrice === lineSellPriceGross && maxPrice === lineSellPriceGross) {
            return null;
        }

        return <LoadThemeComponent defaultView={defaultView} baseView={baseView} />;
    }

    return getContent();
}

export default LinePrice;
